// User-defined attributes: the applicability RULE algebra, in the browser.
//
// The TypeScript twin of twasta_runtime_engine/uda/rules.py — THE single
// algorithm (plan D8). Both run test-script/uda_fixtures/rules/*.json
// (test_uda_rules.py for Python, test_uda_model.py's node section for this
// file), so the setup screen's Rules and Grid editors produce exactly the
// rules the server would. Java needs only covers / setCovers.
//
// A rule maps every deciding-column ("driver") key to one of:
//   '*'                    Any value (a missing key means the same);
//   ['A', 'B']             any one of these codes;
//   { any_except: ['A'] }  every value but these — what removing one cell
//                          from an Any column produces, so a value the master
//                          gains later is still covered.
// A driver whose value is UNKNOWN is accepted only by Any.
//
// Reference: the prototype's normRules / addCell / removeCell / setCovers /
// gridAxes / coverage (docs/design/user-defined-attributes/configuration.html),
// extended with any_except. Pure functions; inputs are never mutated.

import type { UdaRule, UdaRuleValue } from './types'

export const ANY = '*'
export const ANY_EXCEPT = 'any_except'

type Canon = '*' | string[] | { any_except: string[] }
export type CanonRule = Record<string, Canon>
type DriverLike = string | { key?: unknown } | null | undefined
type Combo = Record<string, unknown>

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

// ── codes ───────────────────────────────────────────────────────────────────

/** The comparable spelling of a deciding value, or null when unknown. Codes
 *  compare as text: 12 / 12.0 / "12" agree; blank is unknown, never a code. */
export function codeOf(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return String(value).trim() || null
    if (Number.isInteger(value)) return String(value)
    return String(value).trim() || null
  }
  const text = String(value).trim()
  return text || null
}

/** Driver keys in declaration order (driver objects or bare keys). */
export function driverKeys(drivers: Iterable<DriverLike> | null | undefined): string[] {
  const out: string[] = []
  for (const d of drivers || []) {
    const raw = isPlainObject(d) ? d.key : d
    const key = String(raw ?? '').trim()
    if (key && !out.includes(key)) out.push(key)
  }
  return out
}

function dedupeCodes(values: Iterable<unknown>): string[] {
  const out: string[] = []
  for (const v of values) {
    const c = codeOf(v)
    if (c !== null && !out.includes(c)) out.push(c)
  }
  return out
}

// ── one value ───────────────────────────────────────────────────────────────

/** Canonical form of one rule value (sloppy shapes accepted, as in Python). */
export function canonValue(raw: unknown): Canon {
  if (raw === null || raw === undefined) return ANY
  if (typeof raw === 'string') {
    const s = raw.trim()
    return s === '' || s === ANY ? ANY : [s]
  }
  if (Array.isArray(raw)) return dedupeCodes(raw)
  if (raw instanceof Set) return dedupeCodes(raw)
  if (isPlainObject(raw)) {
    if (ANY_EXCEPT in raw) {
      const excl = raw[ANY_EXCEPT]
      const codes = dedupeCodes(Array.isArray(excl) ? excl : [excl])
      return codes.length ? { any_except: codes } : ANY
    }
    if ('in' in raw) {
      const vals = raw.in
      return dedupeCodes(Array.isArray(vals) ? vals : [vals])
    }
    return ANY
  }
  const c = codeOf(raw)
  return c !== null ? [c] : ANY
}

export const isAny = (v: Canon): v is '*' => v === ANY
export const isExcept = (v: Canon): v is { any_except: string[] } => isPlainObject(v)

/** Whether a canonical value accepts `code` (null = unknown). */
export function accepts(value: Canon, code: string | null): boolean {
  if (value === ANY) return true
  if (code === null) return false
  if (isExcept(value)) return !value.any_except.includes(code)
  return (value as string[]).includes(code)
}

/** `outer` ⊇ `inner` for every possible master. */
export function contains(outer: Canon, inner: Canon): boolean {
  if (outer === ANY) return true
  if (inner === ANY) return false
  if (isExcept(outer)) {
    const excl = outer.any_except
    if (isExcept(inner)) return excl.every(e => inner.any_except.includes(e))
    return !(inner as string[]).some(v => excl.includes(v))
  }
  if (isExcept(inner)) return false
  return (inner as string[]).every(v => (outer as string[]).includes(v))
}

export const same = (a: Canon, b: Canon): boolean => contains(a, b) && contains(b, a)

/** The values accepted by `a` OR `b`, as one canonical value. */
export function union(a: Canon, b: Canon): Canon {
  if (a === ANY || b === ANY) return ANY
  if (isExcept(a) && isExcept(b)) {
    const excl = a.any_except.filter(e => b.any_except.includes(e))
    return excl.length ? { any_except: excl } : ANY
  }
  if (isExcept(a) || isExcept(b)) {
    const exclSide = (isExcept(a) ? a : b) as { any_except: string[] }
    const listSide = (isExcept(a) ? b : a) as string[]
    const excl = exclSide.any_except.filter(e => !listSide.includes(e))
    return excl.length ? { any_except: excl } : ANY
  }
  const la = a as string[]
  return [...la, ...(b as string[]).filter(v => !la.includes(v))]
}

/** `value` without the single code `code`. Removing from Any yields
 *  any_except — never today's value list frozen in. */
export function minus(value: Canon, code: string): Canon {
  if (value === ANY) return { any_except: [code] }
  if (isExcept(value)) {
    const excl = [...value.any_except]
    if (!excl.includes(code)) excl.push(code)
    return { any_except: excl }
  }
  return (value as string[]).filter(v => v !== code)
}

const isEmpty = (v: Canon): boolean => Array.isArray(v) && v.length === 0

// ── one rule ────────────────────────────────────────────────────────────────

/** Canonical rule: every driver key present (in driver order), each value
 *  canonical. Keys naming no declared driver are left out. */
export function canonRule(rule: unknown, drivers: Iterable<DriverLike>): CanonRule {
  const src = isPlainObject(rule) ? rule : {}
  const out: CanonRule = {}
  for (const k of driverKeys(drivers)) out[k] = canonValue(src[k])
  return out
}

function ruleContains(outer: CanonRule, inner: CanonRule, keys: string[]): boolean {
  return keys.every(k => contains(outer[k], inner[k]))
}

/** How many drivers a rule names (non-Any). */
export function specificity(rule: unknown, drivers: Iterable<DriverLike>): number {
  const r = canonRule(rule, drivers)
  return Object.values(r).filter(v => v !== ANY).length
}

/** Whether `rule` covers the record whose deciding values are `combo`. */
export function covers(rule: unknown, combo: Combo | null | undefined, drivers: Iterable<DriverLike>): boolean {
  const c = combo || {}
  const src = isPlainObject(rule) ? rule : {}
  for (const k of driverKeys(drivers)) {
    const value = canonValue(src[k])
    if (value === ANY) continue
    if (!accepts(value, codeOf(c[k]))) return false
  }
  return true
}

export function rulesOf(setOrRules: unknown): Record<string, unknown>[] {
  const raw = isPlainObject(setOrRules) ? setOrRules.rules : setOrRules
  return (Array.isArray(raw) ? raw : []).filter(isPlainObject)
}

/** Whether an attribute set (or a bare rule list) covers `combo`. */
export function setCovers(setOrRules: unknown, combo: Combo | null | undefined, drivers: Iterable<DriverLike>): boolean {
  return rulesOf(setOrRules).some(r => covers(r, combo, drivers))
}

// ── normalisation and editing ───────────────────────────────────────────────

/** The smallest equivalent rule list, deterministically (see rules.py
 *  `normalize`): drop rules matching nothing; then repeatedly drop the first
 *  covered rule, else merge the first pair differing in exactly one driver. */
export function normalize(rules: Iterable<unknown> | null | undefined, drivers: Iterable<DriverLike>): CanonRule[] {
  const keys = driverKeys(drivers)
  let rs = [...(rules || [])].filter(isPlainObject).map(r => canonRule(r, keys))
  rs = rs.filter(r => !Object.values(r).some(isEmpty))
  let changed = true
  while (changed) {
    changed = false
    outer: for (let i = 0; i < rs.length; i++) {
      for (let j = i + 1; j < rs.length; j++) {
        const a = rs[i]
        const b = rs[j]
        if (ruleContains(a, b, keys)) { rs.splice(j, 1); changed = true; break outer }
        if (ruleContains(b, a, keys)) { rs.splice(i, 1); changed = true; break outer }
        const diff = keys.filter(k => !same(a[k], b[k]))
        if (diff.length === 1) {
          const k = diff[0]
          rs[i] = { ...a, [k]: union(a[k], b[k]) }
          rs.splice(j, 1)
          changed = true
          break outer
        }
      }
    }
  }
  return rs
}

function cellCodes(combo: Combo | null | undefined, keys: string[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of keys) {
    const c = codeOf((combo || {})[k])
    if (c === null) {
      throw new Error(`A cell names one value for every deciding column; '${k}' has none`)
    }
    out[k] = c
  }
  return out
}

/** `rules` plus `rule`, normalised (painting a whole row or column). */
export function addRule(rules: Iterable<unknown> | null | undefined, rule: unknown, drivers: Iterable<DriverLike>): CanonRule[] {
  return normalize([...(rules || []), rule], drivers)
}

/** Cover one more combination; a covered cell changes nothing. */
export function addCell(rules: Iterable<unknown> | null | undefined, combo: Combo, drivers: Iterable<DriverLike>): CanonRule[] {
  const keys = driverKeys(drivers)
  const cell = cellCodes(combo, keys)
  const list = [...(rules || [])]
  if (list.some(r => covers(r, cell, keys))) return normalize(list, keys)
  const one: Record<string, string[]> = {}
  for (const k of keys) one[k] = [cell[k]]
  return normalize([...list, one], keys)
}

/** Stop covering one combination: each covering rule is split into the
 *  rectangles that leave it out (on Any that part is any_except). */
export function removeCell(rules: Iterable<unknown> | null | undefined, combo: Combo, drivers: Iterable<DriverLike>): CanonRule[] {
  const keys = driverKeys(drivers)
  const cell = cellCodes(combo, keys)
  const out: CanonRule[] = []
  for (const raw of rules || []) {
    if (!isPlainObject(raw)) continue
    const rule = canonRule(raw, keys)
    if (!covers(rule, cell, keys)) { out.push(rule); continue }
    const prefix: CanonRule = { ...rule }
    for (const k of keys) {
      const rest = minus(rule[k], cell[k])
      if (!isEmpty(rest)) out.push({ ...prefix, [k]: rest })
      prefix[k] = [cell[k]]
    }
  }
  return normalize(out, keys)
}

// ── which sets apply ────────────────────────────────────────────────────────

/** The sets covering `combo`, most specific first, then declaration order. */
export function applicableSets<S extends { key?: unknown; rules?: unknown }>(
  sets: Iterable<S> | null | undefined, combo: Combo | null | undefined, drivers: Iterable<DriverLike>,
): S[] {
  const keys = driverKeys(drivers)
  const hits: Array<[number, number, S]> = []
  let idx = 0
  for (const s of sets || []) {
    const i = idx++
    if (!isPlainObject(s)) continue
    let best = -1
    for (const r of rulesOf(s)) {
      if (covers(r, combo, keys)) best = Math.max(best, specificity(r, keys))
    }
    if (best >= 0) hits.push([-best, i, s])
  }
  hits.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]))
  return hits.map(h => h[2])
}

/** Keys of the sets covering `combo` (prototype `setsFor`). */
export function cellSets(sets: Iterable<{ key?: unknown; rules?: unknown }> | null | undefined,
  combo: Combo | null | undefined, drivers: Iterable<DriverLike>): string[] {
  return applicableSets(sets, combo, drivers).map(s => String(s.key ?? ''))
}

// ── coverage without enumeration ────────────────────────────────────────────

/** How many combinations of the masters' values no set covers. Values of one
 *  driver accepted by the same rules are grouped, so the cost follows the
 *  number of rules, never the size of the combination space. */
export function countUncovered(
  sets: Iterable<unknown> | null | undefined, drivers: Iterable<DriverLike>,
  valuesByDriver: Record<string, unknown[]>, page?: Record<string, unknown> | null,
): number {
  const keys = driverKeys(drivers)
  const pinned = page || {}
  const domains: string[][] = keys.map(k => {
    if (k in pinned) {
      const c = codeOf(pinned[k])
      return c !== null ? [c] : []
    }
    return dedupeCodes(valuesByDriver[k] || [])
  })
  const rules: CanonRule[] = []
  for (const s of sets || []) {
    for (const r of rulesOf(s)) {
      const cr = canonRule(r, keys)
      if (!Object.values(cr).some(isEmpty)) rules.push(cr)
    }
  }
  const suffix: number[] = new Array(keys.length + 1).fill(1)
  for (let i = keys.length - 1; i >= 0; i--) suffix[i] = suffix[i + 1] * domains[i].length
  const memo = new Map<string, number>()
  const count = (i: number, live: number[]): number => {
    if (!live.length) return suffix[i]
    if (i === keys.length) return 0
    const mk = `${i}|${live.join(',')}`
    const hit = memo.get(mk)
    if (hit !== undefined) return hit
    const tail = keys.slice(i)
    if (live.some(x => tail.every(k => rules[x][k] === ANY))) { memo.set(mk, 0); return 0 }
    const k = keys[i]
    const groups = new Map<string, { sig: number[]; n: number }>()
    for (const v of domains[i]) {
      const sig = live.filter(x => accepts(rules[x][k], v))
      const gk = sig.join(',')
      const g = groups.get(gk)
      if (g) g.n += 1
      else groups.set(gk, { sig, n: 1 })
    }
    let total = 0
    groups.forEach(g => { total += g.n * count(i + 1, g.sig) })
    memo.set(mk, total)
    return total
  }
  return count(0, rules.map((_, i) => i))
}

/** `{all, covered, none}` combination counts (prototype `coverage`). */
export function coverage(
  sets: Iterable<unknown> | null | undefined, drivers: Iterable<DriverLike>,
  valuesByDriver: Record<string, unknown[]>, page?: Record<string, unknown> | null,
): { all: number; covered: number; none: number } {
  const keys = driverKeys(drivers)
  const pinned = page || {}
  let total = 1
  for (const k of keys) {
    if (k in pinned) total *= codeOf(pinned[k]) !== null ? 1 : 0
    else total *= dedupeCodes(valuesByDriver[k] || []).length
  }
  if (!keys.length) total = 1
  const none = countUncovered(sets, keys, valuesByDriver, page)
  return { all: total, covered: total - none, none }
}

/** Which driver goes on the Grid's rows, columns and page selector. */
export function gridAxes(drivers: Iterable<DriverLike>, valuesByDriver: Record<string, unknown[]>)
  : { rows: string | null; cols: string | null; page: string | null } {
  const keys = driverKeys(drivers)
  if (!keys.length) return { rows: null, cols: null, page: null }
  if (keys.length === 1) return { rows: keys[0], cols: null, page: null }
  const len = (k: string) => dedupeCodes(valuesByDriver[k] || []).length
  // Stable: a tie keeps declaration order (Python's sorted is stable too).
  const firstTwo = keys.slice(0, 2).map((k, i) => ({ k, i }))
    .sort((a, b) => (len(b.k) - len(a.k)) || (a.i - b.i)).map(x => x.k)
  return { rows: firstTwo[0], cols: firstTwo[1], page: keys.length > 2 ? keys[2] : null }
}

/** A canonical rule as the layer stores it (the shared `UdaRule` shape). */
export function asUdaRule(rule: CanonRule): UdaRule {
  return rule as Record<string, UdaRuleValue>
}
